import apiService from './api';

export interface IntegrationMetrics {
  time_window: {
    start: string;
    end: string;
    hours: number;
  };
  summary: {
    total_events_24h: number;
    success_rate: number;
    avg_processing_time: number;
    active_alerts: number;
  };
  historical: {
    total_events: number;
    success_rate: number;
    failure_rate: number;
    avg_processing_time_ms: number;
    status_breakdown: Record<string, number>;
  };
  real_time: Record<string, unknown>;
}

interface IntegrationHealth {
  integrations: Record<string, {
    health_score: number;
    status: 'healthy' | 'degraded' | 'unhealthy';
    last_success: string | null;
    failure_rate: number;
    non_success_rate?: number;
    server_error_rate?: number;
    avg_response_time: number;
  }>;
  overall_health: {
    score: number;
    status: 'healthy' | 'degraded' | 'unhealthy';
  };
  window_hours: number;
  window_label: string;
  last_updated: string;
}

interface Alert {
  type: string;
  source: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  failure_rate?: number;
  threshold?: number;
  avg_time_ms?: number;
}

interface AlertsResponse {
  alerts: Alert[];
  total_count: number;
  severity_breakdown: Record<string, number>;
}

interface MonitoringDashboard {
  integration_metrics: IntegrationMetrics;
  integration_health: IntegrationHealth;
  alerts: AlertsResponse;
  system_status: {
    monitoring_active: boolean;
    last_updated: string;
    data_retention_days: number;
    alert_count: number;
    integrations_monitored: number;
  };
}

/** Per-binding row from POST /monitoring/webhooks/binding-health */
export interface BindingHealthRow {
  health_score: number;
  status: 'healthy' | 'degraded' | 'unhealthy';
  last_success: string | null;
  failure_rate: number;
  non_success_rate?: number;
  server_error_rate?: number;
  avg_response_time: number;
  total_events: number;
}

export interface BindingHealthResponse {
  bindings: Record<string, BindingHealthRow>;
  window_hours: number;
  window_label: string;
  last_updated: string;
}

interface SystemStatus {
  monitoring_service: string;
  redis_connection: string;
  database_connection: string;
  data_retention: {
    integration_requests: string;
    metrics_summary: string;
    alerts: string;
  };
  features: {
    real_time_metrics: boolean;
    historical_analytics: boolean;
    alert_system: boolean;
    dashboard_api: boolean;
  };
  last_updated: string;
}

class MonitoringService {
  private baseUrl = '/monitoring';

  /**
   * Space-scoped integration request metrics (REST, socket-access, provider webhooks).
   */
  async getIntegrationMetrics(
    integrationType: string | undefined,
    hours: number,
    spaceId: string
  ): Promise<IntegrationMetrics> {
    const params = new URLSearchParams({
      hours: hours.toString(),
      space_id: spaceId,
    });

    if (integrationType) {
      params.append('integration_type', integrationType);
    }

    return await apiService.get(`${this.baseUrl}/webhooks/metrics?${params}`);
  }

  /**
   * @deprecated Use getIntegrationMetrics — same endpoint, clearer name.
   */
  async getWebhookMetrics(
    source: string | undefined,
    hours: number,
    spaceId: string
  ): Promise<IntegrationMetrics> {
    return this.getIntegrationMetrics(source, hours, spaceId);
  }

  async getIntegrationHealth(spaceId: string): Promise<IntegrationHealth> {
    const params = new URLSearchParams({ space_id: spaceId });
    return await apiService.get(`${this.baseUrl}/webhooks/health?${params}`);
  }

  /**
   * Per-binding health for Integrations UI (one batched read on the server).
   */
  async postBindingsHealth(
    spaceId: string,
    bindings: Array<{ binding_id: string; integration_type: string }>
  ): Promise<BindingHealthResponse> {
    if (bindings.length === 0) {
      return {
        bindings: {},
        window_hours: 1,
        window_label: 'last_1h',
        last_updated: '',
      };
    }
    return await apiService.post<BindingHealthResponse>(
      `${this.baseUrl}/webhooks/binding-health`,
      {
        space_id: spaceId,
        bindings,
      }
    );
  }

  async getAlerts(
    severity: string | undefined,
    limit: number,
    spaceId: string
  ): Promise<AlertsResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      space_id: spaceId,
    });

    if (severity) {
      params.append('severity', severity);
    }

    return await apiService.get(`${this.baseUrl}/webhooks/alerts?${params}`);
  }

  async getDashboard(hours: number, spaceId: string): Promise<MonitoringDashboard> {
    const params = new URLSearchParams({
      hours: hours.toString(),
      space_id: spaceId,
    });

    return await apiService.get(`${this.baseUrl}/dashboard?${params}`);
  }

  async getSystemStatus(): Promise<SystemStatus> {
    return await apiService.get(`${this.baseUrl}/system/status`);
  }

  async triggerTestAlert(alertType: string): Promise<{ success: boolean; message: string; timestamp: string }> {
    const params = new URLSearchParams({
      alert_type: alertType,
    });

    return await apiService.post(`${this.baseUrl}/webhooks/test-alert?${params}`);
  }

  /**
   * Metrics for one integration type (e.g. n8n, whatsapp_basic).
   */
  async getIntegrationTypeMetrics(
    integrationType: string,
    hours: number,
    spaceId: string
  ): Promise<IntegrationMetrics> {
    return this.getIntegrationMetrics(integrationType, hours, spaceId);
  }

  async getWebhookTimeline(source?: string, hours: number = 24): Promise<Array<{
    timestamp: string;
    success_count: number;
    failure_count: number;
    avg_processing_time: number;
  }>> {
    const params = new URLSearchParams({
      hours: hours.toString(),
    });

    if (source) {
      params.append('source', source);
    }

    try {
      return await apiService.get(`${this.baseUrl}/webhooks/timeline?${params}`);
    } catch (error) {
      console.warn('Integration timeline endpoint not available:', error);
      return [];
    }
  }

  async getPerformanceBreakdown(
    hours: number,
    spaceId: string
  ): Promise<Array<{
    source: string;
    avg_processing_time: number;
    success_rate: number;
    total_events: number;
    failure_rate: number;
  }>> {
    const params = new URLSearchParams({
      hours: hours.toString(),
      space_id: spaceId,
    });

    try {
      return await apiService.get(`${this.baseUrl}/webhooks/performance?${params}`);
    } catch (error) {
      console.warn('Performance breakdown endpoint not available, using integration health:', error);

      const health = await this.getIntegrationHealth(spaceId);
      return Object.entries(health.integrations).map(([source, data]) => ({
        source,
        avg_processing_time: data.avg_response_time,
        success_rate: 1 - data.failure_rate,
        total_events: 0,
        failure_rate: data.failure_rate,
      }));
    }
  }

  async getAlertHistory(days: number = 7): Promise<Array<{
    date: string;
    critical: number;
    high: number;
    medium: number;
    low: number;
    total: number;
  }>> {
    const params = new URLSearchParams({
      days: days.toString(),
    });

    try {
      return await apiService.get(`${this.baseUrl}/alerts/history?${params}`);
    } catch (error) {
      console.warn('Alert history endpoint not available:', error);
      return [];
    }
  }

  async acknowledgeAlert(alertId: string): Promise<{ success: boolean; message: string }> {
    try {
      return await apiService.post(`${this.baseUrl}/alerts/${alertId}/acknowledge`);
    } catch (error) {
      console.warn('Alert acknowledgment endpoint not available:', error);
      throw error;
    }
  }

  async resolveAlert(alertId: string, resolution?: string): Promise<{ success: boolean; message: string }> {
    try {
      return await apiService.post(`${this.baseUrl}/alerts/${alertId}/resolve`, {
        resolution,
      });
    } catch (error) {
      console.warn('Alert resolution endpoint not available:', error);
      throw error;
    }
  }

  async getRealTimeMetrics(spaceId: string): Promise<{
    integration_requests_per_minute: number;
    current_success_rate: number;
    active_integrations: number;
    current_alerts: number;
    avg_response_time_last_5min: number;
  }> {
    try {
      return await apiService.get(`${this.baseUrl}/real-time`);
    } catch (error) {
      console.warn('Real-time metrics endpoint not available:', error);

      const dashboard = await this.getDashboard(1, spaceId);
      return {
        integration_requests_per_minute:
          dashboard.integration_metrics.summary.total_events_24h / (24 * 60),
        current_success_rate: dashboard.integration_metrics.summary.success_rate,
        active_integrations: Object.keys(dashboard.integration_health.integrations).length,
        current_alerts: dashboard.alerts.total_count,
        avg_response_time_last_5min: dashboard.integration_metrics.summary.avg_processing_time,
      };
    }
  }

  async exportData(
    startDate: string,
    endDate: string,
    format: 'json' | 'csv' = 'json'
  ): Promise<Blob> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      format,
    });

    return await apiService.get(`${this.baseUrl}/export?${params}`, {
      responseType: 'blob',
    });
  }

  async getHealthCheck(): Promise<{
    status: string;
    service: string;
    timestamp: string;
    features: string[];
  }> {
    return await apiService.get(`${this.baseUrl}/health`);
  }
}

export const monitoringService = new MonitoringService();
export default monitoringService;
